import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Input, Button, Switch, Modal, Tag, Space, Table, Popconfirm, Tooltip, Select } from "antd";
import { DeleteFilled, EditFilled, PlusCircleFilled, UserOutlined, SafetyCertificateOutlined, AppstoreOutlined, GlobalOutlined, SwapOutlined, MailOutlined, KeyOutlined, ApiOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import UserIcon from "../../../assets/icons/userOrange.svg";
import PageBreadcrumb from "../../../components/PageBreadcrumb";
import { hideLoader, showLoader } from "../../../utils/loader";
import { customBaseUrl, httpGet, httpPut, httpPost, httpDelete } from "../../../store/http";
import AntDropdown from "../../../components/CustomDropdown/AntDropdown";
import Swal from "sweetalert2";
import moment from "moment";
import { currencyFormat } from "../../../utils/helper";
import UpdateProductPricingModal from "./UpdateProductPricingModal";
import Layout from "../../../components/Layout";
import SubscriptionRequests from "../../B2BProducts/SubscriptionRequests";
import ApiKeyRequests from "./ApiKeyRequests";
import './profilePage.css';


const PERMISSION_OPTIONS = [
  { label: 'VIEW PRODUCTS', value: 'VIEW_PRODUCTS' },
  { label: 'MANAGE API KEYS', value: 'MANAGE_API_KEYS' },
  { label: 'VIEW PRICING', value: 'VIEW_PRICING' },
  { label: 'MANAGE TEAM', value: 'MANAGE_TEAM' },
  { label: 'MANAGE SETTINGS', value: 'MANAGE_SETTINGS' },
  { label: 'VIEW API KEYS', value: 'VIEW_API_KEYS' },
  { label: 'VIEW SETTINGS', value: 'VIEW_SETTINGS' },
  { label: 'EXPORT TRANSACTIONS', value: 'EXPORT_TRANSACTIONS' },
  { label: 'MANAGE WEBHOOKS', value: 'MANAGE_WEBHOOKS' },
  { label: 'VIEW TRANSACTIONS', value: 'VIEW_TRANSACTIONS' },
  { label: 'MANAGE SUBSCRIPTIONS', value: 'MANAGE_SUBSCRIPTIONS' }
];

const ROLE_OPTIONS = [
  { label: 'ADMIN', value: 'ADMIN' },
  { label: 'DEVELOPER', value: 'DEVELOPER' },
  { label: 'ANALYST', value: 'ANALYST' },
  { label: 'VIEWER', value: 'VIEWER' }
];

const ProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // --- State Management ---
  const [merchant, setMerchant] = useState({});
  const [merchantDetails, setMerchantDetails] = useState({});
  const [category, setCategory] = useState('general');

  const [subscribedProducts, setSubscribedProducts] = useState([]);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [selectedProductCodes, setSelectedProductCodes] = useState([]);
  const [showProductDetails, setShowProductDetails] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // IP Management
  const [whitelistedIps, setWhitelistedIps] = useState([]);
  const [showAddIP, setShowAddIP] = useState(false);
  const [ipTextAreaContent, setIpTextAreaContent] = useState('');

  // API Keys & Status
  const [keys, setKeys] = useState([]);
  const [testKeyEnabled, setTestKeyEnabled] = useState(false);
  const [liveKeyEnabled, setLiveKeyEnabled] = useState(false);
  const [merchantActive, setMerchantActive] = useState(false);
  const [reload, setReload] = useState(false);
  const [reloadKeys, setReloadKeys] = useState(false);
  const [showPricingUpdateModal, setShowPricingUpdateModal] = useState(false);

  // Products fetched from API
  const [availableProducts, setAvailableProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);


  // B2B Team Members
  const [showAddTeamMember, setShowAddTeamMember] = useState(false);
  const [showEditTeamMember, setShowEditTeamMember] = useState(false);
  const [selectedTeamMember, setSelectedTeamMember] = useState(null);

  // Add member form state
  const [addFirstName, setAddFirstName] = useState('');
  const [addLastName, setAddLastName] = useState('');
  const [addEmail, setAddEmail] = useState('');
  const [addRole, setAddRole] = useState(null);
  const [addPermissions, setAddPermissions] = useState([]);

  // Edit member form state
  const [editRole, setEditRole] = useState(null);
  const [editPermissions, setEditPermissions] = useState([]);

  const [teamMembers, setTeamMembers] = useState([]);

  const [bulkTransferLimit, setBulkTransferLimit] = useState(100);
  const [bulkTransfers, setBulkTransfers] = useState([]);
  const [loadingBulkData, setLoadingBulkData] = useState(false);

  // Merchant Key & Webhook
  const [merchantKeyValue, setMerchantKeyValue] = useState('');
  const [webhookUrlValue, setWebhookUrlValue] = useState('');
  const [webhookDetails, setWebhookDetails] = useState(null);
  const [savingKey, setSavingKey] = useState(false);
  const [savingWebhook, setSavingWebhook] = useState(false);


  const handleAddTeamMember = async () => {
    showLoader();
    try {
      const data = {
        firstName: addFirstName,
        lastName: addLastName,
        email: addEmail,
        role: addRole,
        permissions: addPermissions
      };
      const res = await httpPost(`${customBaseUrl.integUrl}/api/v1/admin/merchants/${id}/team/invite`, data);
      if (res.status === true) {
        setShowAddTeamMember(false);
        Swal.fire('Invited!', 'Team member has been invited successfully.', 'success');
        fetchTeamMembers();
        setAddFirstName(''); setAddLastName(''); setAddEmail(''); setAddRole(null); setAddPermissions([]);
      } else {
        Swal.fire('Error', res.message || 'Failed to invite team member', 'error');
      }
    } finally {
      hideLoader();
    }
  };

  const handleEditTeamMember = async () => {
    showLoader();
    try {
      const data = {
        role: editRole,
        permissions: editPermissions
      };
      const res = await httpPut(`${customBaseUrl.integUrl}/api/v1/admin/merchants/${id}/team/${selectedTeamMember?.userId}`, data);
      if (res.status === true || !res.error) {
        setShowEditTeamMember(false);
        setSelectedTeamMember(null);
        Swal.fire('Updated!', 'Team member details have been updated.', 'success');
        fetchTeamMembers();
      } else {
        Swal.fire('Error', res.message || 'Failed to update team member', 'error');
      }
    } finally {
      hideLoader();
    }
  };

  const handleDeleteTeamMember = async (memberId) => {
    showLoader();
    try {
      // Assuming delete corresponds to deactivate
      const res = await httpPut(`${customBaseUrl.integUrl}/api/v1/admin/merchants/${id}/team/${memberId}/deactivate`, {});
      if (res.status === true || !res.error) {
        Swal.fire('Deactivated!', 'Team member has been deactivated.', 'success');
        fetchTeamMembers();
      } else {
        Swal.fire('Error', res.message || 'Failed to deactivate team member', 'error');
      }
    } finally {
      hideLoader();
    }
  };

  const handleResendInvite = async (memberId) => {
    showLoader();
    try {
      const res = await httpPost(`${customBaseUrl.integUrl}/api/v1/admin/merchants/${id}/team/${memberId}/resend-invite`, {});
      if (res.status === true || !res.error) {
        Swal.fire('Sent!', 'Invite has been resent.', 'success');
      } else {
        Swal.fire('Error', res.message || 'Failed to resend invite', 'error');
      }
    } finally {
      hideLoader();
    }
  };

  // --- Bulk Transfer Logic ---

  const fetchBulkTransferData = React.useCallback(async () => {
    setLoadingBulkData(true);
    try {
      // Placeholder for fetching bulk transfer history
      // const res = await httpGet(`${customBaseUrl.integUrl}/api/v1/bulk-transfers/merchant/${id}`);
      // if (res.status) setBulkTransfers(res.data);

      // Dummy data for implementation
      setBulkTransfers([]);

      // Placeholder for fetching merchant bulk limit
      // const limitRes = await httpGet(`${customBaseUrl.integUrl}/api/v1/merchants/${id}/bulk-limit`);
      // if (limitRes.status) setBulkTransferLimit(limitRes.data.limit);
    } catch (e) {
      console.error("Failed to fetch bulk transfer data", e);
    } finally {
      setLoadingBulkData(false);
    }
  }, [id]);

  const handleUpdateBulkLimit = async (newLimit) => {
    if (newLimit > 100) {
      return Swal.fire('Error', 'Bulk transfer limit cannot exceed 100 lines per day.', 'error');
    }
    showLoader();
    try {
      // Placeholder for updating bulk transfer limit
      // const res = await httpPut(`${customBaseUrl.integUrl}/api/v1/merchants/${id}/bulk-limit`, { limit: newLimit });
      // if (res.status) {
      //   setBulkTransferLimit(newLimit);
      //   Swal.fire('Success', 'Bulk transfer limit updated successfully.', 'success');
      // }

      setBulkTransferLimit(newLimit);
      Swal.fire('Success', 'Bulk transfer limit updated successfully.', 'success');
    } finally {
      hideLoader();
    }
  };

  const fetchMerchantData = React.useCallback(async () => {
    showLoader();
    try {
      const response = await httpGet(`${customBaseUrl.integUrl}/api/v1/merchants/${id}`);
      if (response.status === true) {
        let data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
        setMerchant(data);
        setSubscribedProducts(data?.subscribedProducts || []);
        setWhitelistedIps(data.whitelistedIps || []);
        setIpTextAreaContent(data.whitelistedIps?.join('\n') || '');
        setTestKeyEnabled(data.testKeyActive);
        setLiveKeyEnabled(data.liveKeyActive);
        setMerchantActive(data.status === 'ACTIVE');

        // Populate Merchant Key & Webhook from merchant details
        setMerchantKeyValue(data?.merchantKey || '');
        setWebhookUrlValue(data?.webhookUrl || '');

        setMerchantDetails({
          address: data?.address,
          city: data?.city,
          dateOfBirth: data?.dateOfBirth,
          email: data?.email,
          firstName: data?.firstName,
          gender: data?.gender,
          middleName: data?.middleName,
          phoneNumber: data?.phoneNumber,
          profileId: data?.id,
          surname: data?.surname
        });
      } else {
        Swal.fire("Error", response.message || "Something went wrong", "error");
      }
    } catch (e) {
      console.error("Failed to fetch merchant data", e);
    } finally {
      hideLoader();
    }
  }, [id]);

  const fetchKeys = React.useCallback(async () => {
    showLoader();
    try {
      const res = await httpGet(`${customBaseUrl.integUrl}/api/v1/merchant-keys/${id}`);
      if (res.status) {
        setKeys(res.data);
      }
    } finally {
      hideLoader();
    }
  }, [id]);


  const fetchTeamMembers = React.useCallback(async () => {
    try {
      const res = await httpGet(`${customBaseUrl.integUrl}/api/v1/admin/merchants/${id}/team?page=0&size=50`);
      if (res.status === true) {
        setTeamMembers(res?.data?.content || res?.data?.members || res?.data || []);
      }
    } catch (e) {
      console.error("Failed to fetch team members", e);
    }
  }, [id]);

  const fetchWebhookDetails = React.useCallback(async () => {
    try {
      const res = await httpGet(`${customBaseUrl.integUrl}/api/v1/merchants/${id}/webhook-details`);
      if (res?.status === true && res?.data) {
        const d = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
        setWebhookDetails(d);
        // Prefer the dedicated webhook-details URL when present
        const url = d?.webhookUrl || d?.url || d?.callbackUrl || d?.notificationUrl;
        if (url) setWebhookUrlValue(url);
      }
    } catch (e) {
      console.error("Failed to fetch webhook details", e);
    }
  }, [id]);

  const loadData = React.useCallback(async () => {
    await Promise.all([
      fetchMerchantData(),
      fetchKeys(),
      fetchBulkTransferData(),
      fetchTeamMembers(),
      fetchWebhookDetails()
    ]);
  }, [fetchMerchantData, fetchKeys, fetchBulkTransferData, fetchTeamMembers, fetchWebhookDetails]);

  const fetchAvailableProducts = React.useCallback(async () => {
    setLoadingProducts(true);
    try {
      const res = await httpGet(`https://servicesv3.staging.wayabank.ng/wayabank-b2b-product-middleware/api/v1/products/active`);
      if (res.status === true && Array.isArray(res.data?.content)) {
        setAvailableProducts(res.data.content);
      }
    } catch (e) {
      console.error("Failed to fetch available products", e);
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    fetchAvailableProducts();
  }, [loadData, fetchAvailableProducts, reload, id]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys, reloadKeys]);

  const handleAddProduct = async () => {
    showLoader();
    try {
      const res = await httpPost(`${customBaseUrl.integUrl}/api/v1/merchants/${id}/products`, {
        productCodes: selectedProductCodes
      });
      if (res.status) {
        setShowAddProduct(false);
        Swal.fire('Done', res?.message, 'success');
        setReload(prev => !prev);
      }
    } finally {
      hideLoader();
    }
  };

  const handleUpdateIPs = async (updatedIpAddresses) => {
    showLoader();
    try {
      const res = await httpPut(`${customBaseUrl.integUrl}/api/v1/merchant-keys/${id}/ip-addresses`, {
        ipAddresses: updatedIpAddresses
      });
      if (res.status === true) {
        setShowAddIP(false);
        Swal.fire('Done', res?.message, 'success');
        setReload(prev => !prev);
      } else {
        Swal.fire('Error', res?.message || 'Failed to update IP addresses', 'error');
      }
    } catch (error) {
      console.error('Error updating IP addresses:', error);
      Swal.fire('Error', 'An error occurred while updating IP addresses', 'error');
    } finally {
      hideLoader();
    }
  };


  const handleUpdateMerchantKey = async () => {
    const key = merchantKeyValue?.trim();
    if (!key) {
      return Swal.fire('Error', 'Please enter a merchant key.', 'error');
    }
    setSavingKey(true);
    showLoader();
    try {
      const res = await httpPut(`${customBaseUrl.integUrl}/api/v1/merchants/${id}/security-key`, {
        merchantKey: key
      });
      if (res?.status === true) {
        Swal.fire('Updated!', res?.message || 'Merchant key updated successfully.', 'success');
        fetchMerchantData();
      } else {
        Swal.fire('Error', res?.message || 'Failed to update merchant key.', 'error');
      }
    } catch (error) {
      console.error('Error updating merchant key:', error);
      Swal.fire('Error', 'An error occurred while updating the merchant key.', 'error');
    } finally {
      setSavingKey(false);
      hideLoader();
    }
  };

  const handleUpdateWebhook = async () => {
    const url = webhookUrlValue?.trim();
    if (!url) {
      return Swal.fire('Error', 'Please enter a webhook URL.', 'error');
    }
    try {
      // eslint-disable-next-line no-new
      new URL(url);
    } catch (e) {
      return Swal.fire('Error', 'Please enter a valid URL (including https://).', 'error');
    }
    setSavingWebhook(true);
    showLoader();
    try {
      const res = await httpPut(`${customBaseUrl.integUrl}/api/v1/merchants/${id}/webhook`, {
        webhookUrl: url
      });
      if (res?.status === true) {
        Swal.fire('Updated!', res?.message || 'Webhook URL updated successfully.', 'success');
        fetchMerchantData();
        fetchWebhookDetails();
      } else {
        Swal.fire('Error', res?.message || 'Failed to update webhook URL.', 'error');
      }
    } catch (error) {
      console.error('Error updating webhook URL:', error);
      Swal.fire('Error', 'An error occurred while updating the webhook URL.', 'error');
    } finally {
      setSavingWebhook(false);
      hideLoader();
    }
  };


  const business = merchant;
  const userData = merchantDetails;
  const user = merchant;
  const products = subscribedProducts;

  const subscribedCodes = (products || []).map(p => p.productCode);
  const notSubscribedProducts = availableProducts.filter(pt => !subscribedCodes.includes(pt.productCode));

  const handleDelete = async (ipAdd) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        const updatedIpAddresses = whitelistedIps.filter(item => item !== ipAdd);
        handleUpdateIPs(updatedIpAddresses);
      }
    });
  };

  const dropDownOptions = ["Set Pricing", "Transactions", "Deactivate Product"];
  const handleDropdownAction = (item, row) => {
    if (item === "Set Pricing") {
      setSelectedProduct(row);
      setShowPricingUpdateModal(true);
    }
    if (item === "Transactions") {
      navigate(`/transactions_history/${row?.productCode}/${merchant?.clientId || id}`, {
        state: {
          merchantName: business?.companyName || `${userData?.surname || ''} ${userData?.firstName || ''}`.trim() || null,
          productName: row?.productName || row?.productCode,
          profileId: id,
        }
      });
    }
    if (item === "Deactivate Product") {
      Swal.fire({
        title: 'Deactivate Product?',
        text: `Are you sure you want to deactivate "${row?.productName || row?.productCode}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#ff6700',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, deactivate it!'
      }).then(async (result) => {
        if (result.isConfirmed) {
          showLoader();
          try {
            const res = await httpDelete(
              `${customBaseUrl.integUrl}/api/v1/merchants/${id}/products/${row?.productCode}`
            );
            if (res.status) {
              Swal.fire('Deactivated!', res?.message || 'Product has been deactivated.', 'success');
              setReload(prev => !prev);
            } else {
              Swal.fire('Error', res?.message || 'Failed to deactivate product.', 'error');
            }
          } catch (error) {
            console.error('Error deactivating product:', error);
            Swal.fire('Error', 'An error occurred while deactivating the product.', 'error');
          } finally {
            hideLoader();
          }
        }
      });
    }
  };


  const handleRegenerateTestKey = async () => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, regenerate it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        showLoader();
        try {
          const response = await httpPost(`${customBaseUrl.integUrl}/api/v1/merchant-keys/${id}/regenerate/test`);
          if (response.status) {
            hideLoader();
            Swal.fire('Regenerated!', 'Test API Key has been regenerated.', 'success');
            setReloadKeys(!reloadKeys);
          } else {
            hideLoader();
            Swal.fire('Error', response.message || 'Failed to regenerate Test API Key', 'error');
          }
        } catch (error) {
          hideLoader();
          console.error('Error regenerating Test API Key:', error);
          Swal.fire('Error', 'An error occurred while regenerating Test API Key', 'error');
        }
      }
    });
  };

  const handleRegenerateLiveKey = async () => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, regenerate it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        showLoader();
        try {
          const response = await httpPost(`${customBaseUrl.integUrl}/api/v1/merchant-keys/${id}/regenerate/live`);
          if (response.status) {
            hideLoader();
            Swal.fire('Regenerated!', 'Production API Key has been regenerated.', 'success');
            setReloadKeys(!reloadKeys);
          } else {
            hideLoader();
            Swal.fire('Error', response.message || 'Failed to regenerate Production API Key', 'error');
          }
        } catch (error) {
          hideLoader();
          console.error('Error regenerating Production API Key:', error);
          Swal.fire('Error', 'An error occurred while regenerating Production API Key', 'error');
        }
      }
    });
  };

  const handleActivateLiveKey = async () => {
    Swal.fire({
      title: 'Are you sure?',
      text: "This will activate the live API key.",
      icon: 'info',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, activate it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        showLoader();
        try {
          const response = await httpPost(`${customBaseUrl.integUrl}/api/v1/merchant-keys/${id}/activate-live`);
          if (response.status) {
            hideLoader();
            Swal.fire('Activated!', 'Live API Key has been activated.', 'success');
            setLiveKeyEnabled(true);
          } else {
            hideLoader();
            Swal.fire('Error', response.message || 'Failed to activate Live API Key', 'error');
          }
        } catch (error) {
          hideLoader();
          console.error('Error activating Live API Key:', error);
          Swal.fire('Error', 'An error occurred while activating Live API Key', 'error');
        }
      }
    });
  };

  const handleToggleLiveKeyStatus = async (checked) => {
    showLoader();
    try {
      const response = await httpPut(`${customBaseUrl.integUrl}/api/v1/merchant-keys/${id}/live-key/status?active=${checked}`);
      if (response.status) {
        hideLoader();
        Swal.fire('Updated!', `Live API Key has been ${checked ? 'enabled' : 'disabled'}.`, 'success');
        setLiveKeyEnabled(checked);
      } else {
        hideLoader();
        Swal.fire('Error', response.message || `Failed to ${checked ? 'enable' : 'disable'} Live API Key`, 'error');
      }
    } catch (error) {
      hideLoader();
      console.error('Error toggling Live API Key status:', error);
      Swal.fire('Error', 'An error occurred while toggling Live API Key status', 'error');
    }
  };

  const handleToggleTestKeyStatus = async (checked) => {
    showLoader();
    try {
      const response = await httpPut(`${customBaseUrl.integUrl}/api/v1/merchant-keys/${id}/test-key/status?active=${checked}`);
      if (response.status) {
        hideLoader();
        Swal.fire('Updated!', `Test API Key has been ${checked ? 'enabled' : 'disabled'}.`, 'success');
        setTestKeyEnabled(checked);
      } else {
        hideLoader();
        Swal.fire('Error', response.message || `Failed to ${checked ? 'enable' : 'disable'} Test API Key`, 'error');
      }
    } catch (error) {
      hideLoader();
      console.error('Error toggling Test API Key status:', error);
      Swal.fire('Error', 'An error occurred while toggling Test API Key status', 'error');
    }
  };

  const handleToggleMerchantStatus = async (checked) => {
    showLoader();
    try {
      const response = await httpPut(`${customBaseUrl.integUrl}/api/v1/merchant-keys/${id}/status?active=${checked}`);
      if (response.status) {
        hideLoader();
        Swal.fire('Updated!', `Merchant status has been set to ${checked ? 'Active' : 'Inactive'}.`, 'success');
        setMerchantActive(checked);
      } else {
        hideLoader();
        Swal.fire('Error', response.message || `Failed to update merchant status`, 'error');
      }
    } catch (error) {
      hideLoader();
      console.error('Error toggling merchant status:', error);
      Swal.fire('Error', 'An error occurred while toggling merchant status', 'error');
    }
  };


  return (
    <Layout>
      <div className="profile-page-wrapper">
        <>
          <PageBreadcrumb
            items={[
              { label: 'Users', path: '/users' },
              { label: business?.companyName || `${userData?.surname || ''} ${userData?.firstName || ''}`.trim() || 'Business Profile' },
            ]}
          />

          {/* ── Header ──────────────────────────────────────────────── */}
          <div className="profile-page-header">
            <div className="profile-page-header-left">
              <div className="profile-page-icon">
                <img
                  src={user?.profileImage?.length > 0 ? user?.profileImage : UserIcon}
                  alt="merchant"
                />
              </div>
              <div>
                <h2 className="profile-page-title">
                  {business?.companyName || `${userData?.surname || ''} ${userData?.firstName || ''}`.trim() || 'Business Profile'}
                </h2>
                <p className="profile-page-subtitle">
                  {business?.email || userData?.email || 'Merchant Business Profile'}
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Switch
                checked={merchantActive}
                onChange={handleToggleMerchantStatus}
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                style={{ alignSelf: 'center' }}
              />
            </div>
          </div>

          {/* ── Stat Cards ─────────────────────────────────────────── */}
          <div className="profile-stats-row">
            <div className="profile-stat-card stat-brand">
              <span className="profile-stat-value">{products?.length || 0}</span>
              <span className="profile-stat-label">Subscribed Products</span>
            </div>
            <div className="profile-stat-card stat-active">
              <span className="profile-stat-value" style={{ color: merchantActive ? '#52c41a' : '#ff4d4f' }}>
                {merchantActive ? 'Active' : 'Inactive'}
              </span>
              <span className="profile-stat-label">Merchant Status</span>
            </div>
            <div className="profile-stat-card">
              <span className="profile-stat-value">{whitelistedIps?.length || 0}</span>
              <span className="profile-stat-label">Whitelisted IPs</span>
            </div>
            <div className="profile-stat-card">
              <span className="profile-stat-value">{teamMembers?.length || 0}</span>
              <span className="profile-stat-label">Team Members</span>
            </div>
            <div className="profile-stat-card stat-brand">
              <span className="profile-stat-value">{bulkTransferLimit}</span>
              <span className="profile-stat-label">Bulk Transfer Limit / Day</span>
            </div>
          </div>

          {/* ── Navigation Tabs ─────────────────────────────────────── */}
          <div className="profile-nav-bar">
            {[
              { key: 'general', label: 'General', icon: <UserOutlined /> },
              { key: 'bulk_transfers', label: 'Bulk Transfers', icon: <SwapOutlined /> },
              { key: 'pricing', label: 'Products', icon: <AppstoreOutlined /> },
              { key: 'ip', label: 'IP Address', icon: <GlobalOutlined /> },
              { key: 'keys', label: 'API Keys', icon: <SafetyCertificateOutlined /> },
              { key: 'merchant_key', label: 'Merchant Key', icon: <KeyOutlined /> },
              { key: 'webhook', label: 'Webhook', icon: <ApiOutlined /> },
            ].map(tab => (
              <Button
                key={tab.key}
                className={`profile-nav-btn${category === tab.key ? ' active' : ''}`}
                icon={tab.icon}
                onClick={() => setCategory(tab.key)}
              >
                {tab.label}
              </Button>
            ))}
          </div>

          {/* ── Category Content ─────────────────────────────────── */}

          {/* GENERAL TAB */}
          {category === 'general' && (
            <>
              {/* Business Info Card */}
              <div className="profile-content-card">
                <div className="profile-section-title">Business Information</div>
                <div className="profile-info-grid">
                  <div className="profile-info-item">
                    <span className="profile-info-label">Business Name</span>
                    <span className="profile-info-value">{business?.companyName ?? 'N/A'}</span>
                  </div>
                  <div className="profile-info-item">
                    <span className="profile-info-label">Waya Account Number</span>
                    <span className="profile-info-value">{business?.wayaBankAccountNumber ?? 'N/A'}</span>
                  </div>
                  <div className="profile-info-item">
                    <span className="profile-info-label">Business Email</span>
                    <span className="profile-info-value">{business?.email ?? 'N/A'}</span>
                  </div>
                  <div className="profile-info-item">
                    <span className="profile-info-label">Business Phone</span>
                    <span className="profile-info-value">{business?.phoneNumber ?? 'N/A'}</span>
                  </div>
                  <div className="profile-info-item">
                    <span className="profile-info-label">Business Address</span>
                    <span className="profile-info-value">{business?.address ?? 'N/A'}</span>
                  </div>
                  <div className="profile-info-item">
                    <span className="profile-info-label">Account Holder Name</span>
                    <span className="profile-info-value">{business?.accountHolderName ?? 'N/A'}</span>
                  </div>
                  <div className="profile-info-item">
                    <span className="profile-info-label">Date Created</span>
                    <span className="profile-info-value">
                      {business?.createdAt ? moment(business.createdAt).format('YYYY-MM-DD') : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              {/* ── B2B Team Members ──────────────────────── */}
              <div className="profile-content-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div className="profile-section-title" style={{ margin: 0 }}>B2B Team Members</div>
                  <Button
                    icon={<PlusCircleFilled />}
                    className="profile-action-btn"
                    onClick={() => setShowAddTeamMember(true)}
                  >
                    Add Member
                  </Button>
                </div>
                <div className="profile-table-card" style={{ boxShadow: 'none', border: '1px solid #f0f0f0' }}>
                  <Table
                    className="profile-table"
                    dataSource={teamMembers}
                    rowKey="userId"
                    size="middle"
                    pagination={{ pageSize: 5, showSizeChanger: false }}
                    columns={[
                      { title: 'Name', key: 'name', render: (_, record) => <span style={{ fontWeight: 600 }}>{record.firstName} {record.lastName}</span> },
                      { title: 'Email', dataIndex: 'email', key: 'email' },
                      {
                        title: 'Role', dataIndex: 'role', key: 'role',
                        render: v => <Tag style={{ background: '#fff3eb', color: '#ff6700', border: '1px solid #ffbb96', borderRadius: 20, fontWeight: 600 }}>{v}</Tag>
                      },
                      {
                        title: 'Status', dataIndex: 'status', key: 'status',
                        render: v => <Tag style={{ background: v === 'ACTIVE' ? '#f6ffed' : '#fff2f0', color: v === 'ACTIVE' ? '#52c41a' : '#ff4d4f', border: `1px solid ${v === 'ACTIVE' ? '#b7eb8f' : '#ffa39e'}`, borderRadius: 20, fontWeight: 600 }}>{v}</Tag>
                      },
                      {
                        title: 'Actions', key: 'actions', width: 120,
                        render: (_, record) => (
                          <Space>
                            <EditFilled
                              style={{ cursor: 'pointer', color: '#ff6700', fontSize: 16 }}
                              onClick={() => {
                                setSelectedTeamMember(record);
                                setEditRole(record.role);
                                setEditPermissions(record.permissions || []);
                                setShowEditTeamMember(true);
                              }}
                            />
                            {record.status !== 'ACTIVE' && (
                              <Tooltip title="Resend Invite">
                                <MailOutlined
                                  style={{ cursor: 'pointer', color: '#ff6700', fontSize: 16 }}
                                  onClick={() => handleResendInvite(record.userId)}
                                />
                              </Tooltip>
                            )}
                            <Popconfirm
                              title="Deactivate this team member?"
                              onConfirm={() => handleDeleteTeamMember(record.userId)}
                              okText="Yes" cancelText="No" okButtonProps={{ danger: true }}
                            >
                              <DeleteFilled style={{ cursor: 'pointer', color: '#ff4d4f', fontSize: 16 }} />
                            </Popconfirm>
                          </Space>
                        )
                      }
                    ]}
                  />
                </div>
              </div>

              {/* Add/Edit Team Member Modals */}
              <Modal
                title="Invite B2B Team Member"
                open={showAddTeamMember}
                onCancel={() => setShowAddTeamMember(false)}
                footer={null}
                width={520}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
                  <div><label style={{ fontWeight: 500, display: 'block', marginBottom: 4 }}>First Name</label><Input size="large" placeholder="Enter first name" value={addFirstName} onChange={e => setAddFirstName(e.target.value)} /></div>
                  <div><label style={{ fontWeight: 500, display: 'block', marginBottom: 4 }}>Last Name</label><Input size="large" placeholder="Enter last name" value={addLastName} onChange={e => setAddLastName(e.target.value)} /></div>
                  <div><label style={{ fontWeight: 500, display: 'block', marginBottom: 4 }}>Email</label><Input size="large" placeholder="Enter email address" value={addEmail} onChange={e => setAddEmail(e.target.value)} /></div>

                  <div>
                    <label style={{ fontWeight: 500, display: 'block', marginBottom: 4 }}>Role</label>
                    <Select
                      size="large"
                      style={{ width: '100%' }}
                      placeholder="Select a role"
                      options={ROLE_OPTIONS}
                      value={addRole}
                      onChange={val => setAddRole(val)}
                    />
                  </div>

                  <div>
                    <label style={{ fontWeight: 500, display: 'block', marginBottom: 4 }}>Permissions</label>
                    <Select
                      mode="multiple"
                      size="large"
                      style={{ width: '100%' }}
                      placeholder="Select permissions"
                      options={PERMISSION_OPTIONS}
                      value={addPermissions}
                      onChange={val => setAddPermissions(val)}
                    />
                  </div>

                  <Button size="large" type="primary" onClick={handleAddTeamMember}
                    style={{ background: '#ff6700', borderColor: '#ff6700', marginTop: 8 }}>
                    Invite Member
                  </Button>
                </div>
              </Modal>

              <Modal
                title="Edit B2B Team Member Role & Permissions"
                open={showEditTeamMember}
                onCancel={() => { setShowEditTeamMember(false); setSelectedTeamMember(null); }}
                footer={null}
                width={520}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
                  <div>
                    <label style={{ fontWeight: 500, display: 'block', marginBottom: 4 }}>Member</label>
                    <Input size="large" disabled value={`${selectedTeamMember?.firstName || ''} ${selectedTeamMember?.lastName || ''} (${selectedTeamMember?.email || ''})`} />
                  </div>

                  <div>
                    <label style={{ fontWeight: 500, display: 'block', marginBottom: 4 }}>Role</label>
                    <Select
                      size="large"
                      style={{ width: '100%' }}
                      placeholder="Select a role"
                      options={ROLE_OPTIONS}
                      value={editRole}
                      onChange={val => setEditRole(val)}
                    />
                  </div>

                  <div>
                    <label style={{ fontWeight: 500, display: 'block', marginBottom: 4 }}>Permissions</label>
                    <Select
                      mode="multiple"
                      size="large"
                      style={{ width: '100%' }}
                      placeholder="Select permissions"
                      options={PERMISSION_OPTIONS}
                      value={editPermissions}
                      onChange={val => setEditPermissions(val)}
                    />
                  </div>

                  <Button size="large" type="primary" onClick={handleEditTeamMember}
                    style={{ background: '#ff6700', borderColor: '#ff6700', marginTop: 8 }}>
                    Update Team Member
                  </Button>
                </div>
              </Modal>
            </>
          )}

          {/* BULK TRANSFERS TAB */}
          {category === 'bulk_transfers' && (
            <>
              <div className="profile-content-card">
                <div className="profile-section-title">Manage Bulk Transfer Limit</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <span style={{ fontWeight: 500, color: '#595959' }}>Daily limit (max 100 lines):</span>
                  <Input
                    type="number"
                    value={bulkTransferLimit}
                    onChange={e => setBulkTransferLimit(parseInt(e.target.value) || 0)}
                    style={{ width: 120, borderRadius: 8 }}
                  />
                  <Button
                    className="profile-action-btn"
                    onClick={() => handleUpdateBulkLimit(bulkTransferLimit)}
                  >
                    Update Limit
                  </Button>
                </div>
              </div>
              <div className="profile-table-card">
                <Table
                  className="profile-table"
                  dataSource={bulkTransfers}
                  rowKey="id"
                  loading={loadingBulkData}
                  size="middle"
                  pagination={{ pageSize: 10, showSizeChanger: true }}
                  columns={[
                    {
                      title: 'Batch ID', dataIndex: 'batchId', key: 'batchId',
                      render: v => <Tag style={{ fontFamily: 'monospace', background: '#fff3eb', color: '#ff6700', border: '1px solid #ffbb96', borderRadius: 6, fontWeight: 600 }}>{v}</Tag>
                    },
                    { title: 'Lines', dataIndex: 'lineCount', key: 'lineCount' },
                    { title: 'Total Amount', dataIndex: 'totalAmount', key: 'totalAmount', render: v => currencyFormat(v) },
                    {
                      title: 'Status', dataIndex: 'status', key: 'status',
                      render: v => <Tag style={{ background: v === 'Completed' ? '#f6ffed' : '#fffbe6', color: v === 'Completed' ? '#52c41a' : '#faad14', border: `1px solid ${v === 'Completed' ? '#b7eb8f' : '#ffe58f'}`, borderRadius: 20, fontWeight: 600 }}>{v}</Tag>
                    },
                    { title: 'Date', dataIndex: 'createdAt', key: 'createdAt', render: v => moment(v).format('YYYY-MM-DD HH:mm') },
                  ]}
                />
              </div>
            </>
          )}

          {/* PRODUCTS TAB */}
          {category === 'pricing' && (
            <>
              {notSubscribedProducts.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                  <Button
                    icon={<PlusCircleFilled />}
                    className="profile-action-btn"
                    onClick={() => setShowAddProduct(true)}
                    style={{ background: '#fff3eb', color: '#ff6700', borderColor: '#ffbb96', fontWeight: 600 }}
                    loading={loadingProducts}
                  >
                    Add New Product
                  </Button>
                </div>
              )}

              <div className="profile-table-card">
                <Table
                  className="profile-table"
                  dataSource={products}
                  rowKey="productCode"
                  size="middle"
                  pagination={{ pageSize: 10, showSizeChanger: true }}
                  columns={[
                    {
                      title: 'Product Name', dataIndex: 'productName', key: 'productName',
                      render: v => <span style={{ fontWeight: 600, color: '#1a1a1a' }}>{v}</span>
                    },
                    {
                      title: 'Product Code', dataIndex: 'productCode', key: 'productCode',
                      render: v => <Tag style={{ fontFamily: 'monospace', background: '#fff3eb', color: '#ff6700', border: '1px solid #ffbb96', borderRadius: 6, fontWeight: 600 }}>{v}</Tag>
                    },
                    {
                      title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true,
                      render: v => <span style={{ color: '#595959' }}>{v || '—'}</span>
                    },
                    {
                      title: 'Status', dataIndex: 'status', key: 'status',
                      render: v => {
                        const s = (v || '').toLowerCase();
                        const cfg = { active: { c: '#52c41a', bg: '#f6ffed', b: '#b7eb8f' }, inactive: { c: '#ff4d4f', bg: '#fff2f0', b: '#ffa39e' } }[s] || { c: '#8c8c8c', bg: '#f5f5f5', b: '#d9d9d9' };
                        return <Tag style={{ color: cfg.c, background: cfg.bg, border: `1px solid ${cfg.b}`, borderRadius: 20, fontWeight: 600, fontSize: 11, padding: '2px 10px' }}>{(v || '—').toUpperCase()}</Tag>;
                      }
                    },
                    {
                      title: 'Actions', dataIndex: 'actions', key: 'actions', width: 130,
                      render: (_, row) => (
                        <AntDropdown
                          dropDownOptions={dropDownOptions}
                          handleDropdownAction={(item) => handleDropdownAction(item, row)}
                          isArrow
                        />
                      )
                    },
                  ]}
                />
              </div>

              {/* ── Product Subscription Requests ──────────────────────── */}
              <div style={{ marginTop: 32 }}>
                <div className="profile-section-title">Product Subscription Requests</div>
                <SubscriptionRequests merchantId={id} />
              </div>


              {showAddProduct && (
                <Modal
                  open={showAddProduct}
                  onCancel={() => {
                    setShowAddProduct(false);
                    setSelectedProductCodes([]);
                  }}
                  footer={null}
                  closable={false}
                  width={650}
                  bodyStyle={{ padding: 0, borderRadius: 16, overflow: 'hidden' }}
                >
                  {/* ── Premium Modal Header ── */}
                  <div style={{
                    background: 'linear-gradient(135deg, #ff6700 0%, #e65c00 60%, #cc5200 100%)',
                    padding: '24px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    position: 'relative',
                  }}>
                    <div style={{ position: 'relative', zIndex: 1, display: 'flex', gap: 14, alignItems: 'center' }}>
                      <div style={{
                        width: 48, height: 48, borderRadius: 12,
                        background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)',
                        border: '1px solid rgba(255,255,255,0.3)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 22, color: '#fff',
                      }}>
                        <AppstoreOutlined />
                      </div>
                      <div>
                        <h3 style={{ color: '#fff', margin: 0, fontSize: 18, fontWeight: 700 }}>Add Products</h3>
                        <p style={{ color: 'rgba(255,255,255,0.8)', margin: 0, fontSize: 12, marginTop: 2 }}>
                          Select new services to activate for {business?.companyName || 'this merchant'}
                        </p>
                      </div>
                    </div>
                    <div
                      style={{ color: '#fff', fontSize: 16, cursor: 'pointer', zIndex: 1, padding: 4 }}
                      onClick={() => { setShowAddProduct(false); setSelectedProductCodes([]); }}
                    >
                      ✕
                    </div>
                  </div>

                  {/* ── Modal Body ── */}
                  <div style={{ padding: '24px' }}>
                    <div style={{
                      fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px',
                      color: '#ff6700', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6,
                    }}>
                      <SafetyCertificateOutlined /> Available Products ({notSubscribedProducts.length})
                    </div>

                    <div style={{
                      background: '#fafafa', borderRadius: 12, padding: '16px 18px',
                      border: '1.5px solid #f0f0f0', marginBottom: 24,
                      display: 'flex', flexDirection: 'column', gap: 12,
                      maxHeight: '450px', overflowY: 'auto'
                    }}>
                      {notSubscribedProducts.map(type => {
                        const productVal = type.name || type.productCode || type.id;
                        const productLabel = type.description || type.name || productVal;

                        return (
                          <label
                            key={productVal}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 12,
                              cursor: 'pointer', padding: '8px 12px',
                              background: selectedProductCodes.includes(productVal) ? '#fff3eb' : '#fff',
                              border: `1.5px solid ${selectedProductCodes.includes(productVal) ? '#ffbb96' : '#e8e8e8'}`,
                              borderRadius: 8, transition: 'all 0.2s',
                            }}
                          >
                            <input
                              type="checkbox"
                              style={{ width: 16, height: 16, accentColor: '#ff6700' }}
                              checked={selectedProductCodes.includes(productVal)}
                              onChange={(e) => {
                                if (e.target.checked) setSelectedProductCodes(prev => [...prev, productVal]);
                                else setSelectedProductCodes(prev => prev.filter(c => c !== productVal));
                              }}
                            />
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <span style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>{productVal}</span>
                              <span style={{ fontSize: 11, color: '#8c8c8c', fontFamily: 'monospace' }}>{productLabel}</span>
                            </div>
                          </label>
                        )
                      })}
                    </div>

                    <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                      <Button
                        style={{ height: 44, borderRadius: 8, fontWeight: 600, px: 20 }}
                        onClick={() => { setShowAddProduct(false); setSelectedProductCodes([]); }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="primary"
                        onClick={handleAddProduct}
                        disabled={selectedProductCodes.length === 0}
                        style={{
                          background: selectedProductCodes.length === 0 ? '#f5f5f5' : '#ff6700',
                          borderColor: selectedProductCodes.length === 0 ? '#d9d9d9' : '#ff6700',
                          color: selectedProductCodes.length === 0 ? '#b8b8b8' : '#fff',
                          height: 44, borderRadius: 8, fontWeight: 600, padding: '0 24px',
                        }}
                      >
                        Activate {selectedProductCodes.length > 0 ? selectedProductCodes.length : ''} Product{selectedProductCodes.length !== 1 ? 's' : ''}
                      </Button>
                    </div>
                  </div>
                </Modal>
              )}
            </>
          )}

          {/* IP ADDRESS TAB */}
          {category === 'ip' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                <Button
                  icon={<PlusCircleFilled />}
                  className="profile-action-btn"
                  onClick={() => setShowAddIP(true)}
                >
                  Update IP Addresses
                </Button>
              </div>
              <div className="profile-content-card">
                <div className="profile-section-title">Whitelisted IP Addresses</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                  {whitelistedIps?.length > 0
                    ? whitelistedIps.map((ip, i) => (
                      <span key={i} className="profile-ip-tag">
                        {ip}
                        <DeleteFilled
                          style={{ cursor: 'pointer', fontSize: 11, color: '#ff4d4f' }}
                          onClick={() => handleDelete(ip)}
                        />
                      </span>
                    ))
                    : <span style={{ color: '#8c8c8c', fontSize: 13 }}>No whitelisted IP addresses configured.</span>
                  }
                </div>
              </div>

              {showAddIP && (
                <Modal title="Update IP Addresses" open={showAddIP}
                  onCancel={() => setShowAddIP(false)} footer={null} width={600}
                >
                  <p style={{ color: '#595959', marginBottom: 12 }}>Enter one IP address per line.</p>
                  <Input.TextArea
                    rows={10}
                    value={ipTextAreaContent}
                    onChange={e => setIpTextAreaContent(e.target.value)}
                    placeholder="e.g. 192.168.1.1"
                    style={{ borderRadius: 8, fontFamily: 'monospace' }}
                  />
                  <Button
                    type="primary"
                    onClick={() => {
                      const list = ipTextAreaContent.split('\n').map(ip => ip.trim()).filter(ip => ip !== '');
                      handleUpdateIPs(list);
                    }}
                    style={{ marginTop: 16, background: '#ff6700', borderColor: '#ff6700' }}
                  >
                    Save IP Addresses
                  </Button>
                </Modal>
              )}
            </>
          )}

          {/* API KEYS TAB */}
          {category === 'keys' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              <div className="profile-content-card">
                <div className="profile-section-title">API Key Management</div>
                <div className="profile-toggle-row">
                  <span className="profile-toggle-label">Test Key Status</span>
                  <Switch checked={testKeyEnabled} onChange={handleToggleTestKeyStatus} checkedChildren="Enabled" unCheckedChildren="Disabled" />
                </div>
                <div className="profile-toggle-row">
                  <span className="profile-toggle-label">Live Key Status</span>
                  <Switch checked={liveKeyEnabled} onChange={handleToggleLiveKeyStatus} checkedChildren="Enabled" unCheckedChildren="Disabled" />
                </div>
                <div style={{ display: 'flex', gap: 12, marginTop: 20, flexWrap: 'wrap' }}>
                  <Button className="profile-action-btn" onClick={handleRegenerateTestKey}>
                    Regenerate Test Key
                  </Button>
                  <Button className="profile-action-btn" onClick={handleRegenerateLiveKey}>
                    Regenerate Live Key
                  </Button>
                  <Button className="profile-action-btn" onClick={handleActivateLiveKey}>
                    Activate Live Key
                  </Button>
                </div>
              </div>

              <div className="profile-content-card">
                <div className="profile-section-title">Live API Key Requests</div>
                <ApiKeyRequests merchantId={id} />
              </div>
            </div>
          )}

          {/* MERCHANT KEY TAB */}
          {category === 'merchant_key' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              <div className="profile-content-card">
                <div className="profile-section-title">Merchant / Security Key</div>
                <p style={{ color: '#8c8c8c', fontSize: 13, marginTop: -8, marginBottom: 16 }}>
                  View and update the merchant's security key used to sign and verify requests. (Admin only)
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 640 }}>
                  <label style={{ fontWeight: 500 }}>Merchant Key</label>
                  <Input.Password
                    size="large"
                    value={merchantKeyValue}
                    onChange={e => setMerchantKeyValue(e.target.value)}
                    placeholder={merchant?.merchantKey ? '' : 'No merchant key set — enter a new key'}
                    style={{ borderRadius: 8, fontFamily: 'monospace' }}
                  />
                  <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                    {merchant?.merchantKey
                      ? 'A merchant key is currently set for this merchant.'
                      : 'No merchant key is currently set for this merchant.'}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
                  <Button
                    type="primary"
                    loading={savingKey}
                    onClick={handleUpdateMerchantKey}
                    style={{ background: '#ff6700', borderColor: '#ff6700' }}
                  >
                    Update Merchant Key
                  </Button>
                  <Button onClick={() => setMerchantKeyValue(merchant?.merchantKey || '')}>
                    Reset
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* WEBHOOK TAB */}
          {category === 'webhook' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              <div className="profile-content-card">
                <div className="profile-section-title">Webhook URL</div>
                <p style={{ color: '#8c8c8c', fontSize: 13, marginTop: -8, marginBottom: 16 }}>
                  The endpoint where transaction and event notifications are delivered. (Admin only)
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 720 }}>
                  <label style={{ fontWeight: 500 }}>Notification URL</label>
                  <Input
                    size="large"
                    value={webhookUrlValue}
                    onChange={e => setWebhookUrlValue(e.target.value)}
                    placeholder="https://yourapp.com/webhook"
                    style={{ borderRadius: 8, fontFamily: 'monospace' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
                  <Button
                    type="primary"
                    loading={savingWebhook}
                    onClick={handleUpdateWebhook}
                    style={{ background: '#ff6700', borderColor: '#ff6700' }}
                  >
                    Update Webhook URL
                  </Button>
                  <Button onClick={fetchWebhookDetails}>
                    Refresh
                  </Button>
                </div>
              </div>

              {webhookDetails && Object.keys(webhookDetails).length > 0 && (
                <div className="profile-content-card">
                  <div className="profile-section-title">Webhook Details</div>
                  <div className="profile-info-grid">
                    {Object.entries(webhookDetails)
                      .filter(([, v]) => v === null || typeof v !== 'object')
                      .map(([k, v]) => (
                        <div className="profile-info-item" key={k}>
                          <span className="profile-info-label">{k}</span>
                          <span className="profile-info-value">{v === null || v === '' ? 'N/A' : String(v)}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>

        {/* ── Modals ── */}
        {showPricingUpdateModal && (
          <UpdateProductPricingModal
            showModal={showPricingUpdateModal}
            hideModal={() => setShowPricingUpdateModal(false)}
            selectedProduct={selectedProduct}
            merchantId={id}
            reload={loadData}
          />
        )}
      </div>
    </Layout>
  );
};

export default ProfilePage;
